import React, { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Divider } from '@mui/material';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import TwBackdrop from '../../../components/TwBackdrop/TwBackdrop';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwDialog from '../../../components/TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import DetailFile from '../../../components/TwLoadDetail/DetailFile/DetailFile';
import TwTagsField, { TwTagsOption } from '../../../components/TwTagsField/TwTagsField';
import StringUtils from '../../../utils/stringUtils';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
import { DefaultInvoiceEmailBodyCopy, InvoiceSubjectLine, TenantAccountingSettings } from '../../../providers/User/User.model';
import { InvoiceSources } from '../../../redux/api/invoiceService/invoiceService.model';
import TwDeleteInvoice from '../../../components/TwDeleteInvoice/TwDeleteInvoice';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import { useGetDivisionByIdQuery } from '../../../redux/api/divisions/divisionsApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { OrderAttachment } from '../../../redux/api/orders/orders.model';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import InfoIcon from '@mui/icons-material/Info';

export interface PreviewInvoiceSendData {
  emailSubject: string | undefined;
  emailBody: string | undefined;
  ccList: string[];
}

export interface PreviewInvoiceTenantData {
  userName: string;
  companyName: string;
}

interface PreviewInvoiceProps {
  open: boolean;
  invoiceNumber?: string | undefined;
  customerOrderNumber: string;
  hasBeenSent?: boolean;
  tenantData: PreviewInvoiceTenantData;
  customerName?: string;
  onClose?: (doCloseInvoicePage: boolean) => void;
  canSendInvoice?: (previewData: PreviewInvoiceSendData) => boolean;
  onInvoiceSent?: (previewData: PreviewInvoiceSendData) => void;
  attachments?: OrderAttachment[];
  onSelectedEmailTo?: (emails: string[]) => void;
  onSelectedEmailCc?: (emails: string[]) => void;
  onSelectedAdditionalEmailsChange?: (emails: string[]) => void;
  presetEmailTo?: string[];
  presetEmailCc?: string[];
  presetQboEmailTo?: string[];
  presetAdditionalQBOEmails?: string[];
  suggestedContacts?: TwTagsOption[];
  invoiceDialogClosed?: boolean;
  loading?: boolean;
  tenantAccountingSettings?: TenantAccountingSettings;
  source?: InvoiceSources;
  invoiceId?: string;
  onQBOInvoiceSent?: (invoiceId: string, previewData?: PreviewInvoiceSendData) => void;
  onSaveClick?: () => void;
  onEditClick?: (invoiceId: string) => void;
  onInvoiceDeleted?: () => void;
  divisionId: string;
  factorInvoice?: boolean;
}

const PreviewInvoice: FC<PreviewInvoiceProps> = ({
  open,
  attachments,
  presetEmailTo,
  presetEmailCc,
  presetQboEmailTo,
  presetAdditionalQBOEmails,
  suggestedContacts,
  invoiceNumber,
  tenantData,
  customerName,
  onSelectedEmailTo,
  onSelectedEmailCc,
  onSelectedAdditionalEmailsChange,
  loading,
  invoiceDialogClosed,
  hasBeenSent,
  onClose,
  onInvoiceSent,
  canSendInvoice,
  customerOrderNumber,
  tenantAccountingSettings,
  source = InvoiceSources.TB,
  invoiceId,
  onQBOInvoiceSent,
  onSaveClick,
  onEditClick,
  onInvoiceDeleted,
  divisionId,
  factorInvoice
}) => {
  const {
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();
  const { enableSendQboInvoiceInternally } = useFeatureFlags();

  const { data: division } = useGetDivisionByIdQuery(divisionId ? divisionId : skipToken);

  const [emailSubject, setEmailSubject] = useState<string>();
  const [emailBody, setEmailBody] = useState<string>();
  const [hasDefaultEmailBody, setHasDefaultEmailBody] = useState<boolean>(true);
  const [ccList, setCcList] = useState<string[]>([]);
  const [emailWarning, setEmailWarning] = useState<boolean>(true);
  const [deleteInvoiceOpen, setDeleteInvoiceOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!customerName) {
      return;
    }

    if (hasDefaultEmailBody) {
      const prefilledBody = getPrefilledBody(customerName, tenantData, tenantAccountingSettings, division?.name);
      setEmailBody(prefilledBody);
    }
  }, [customerName, tenantData, division?.name, tenantAccountingSettings]);

  useEffect(() => {
    const subjectLine =
      tenantAccountingSettings?.invoice_subject_line || InvoiceSubjectLine.INVOICE_NUMBER;
    const companyName = division?.name;
    let text = '';

    if (!!hasBeenSent) {
      text = `Resending invoice #${invoiceNumber} from ${companyName}`;

      switch (subjectLine) {
        case InvoiceSubjectLine.INVOICE_NUMBER:
          break;
        case InvoiceSubjectLine.CUSTOMER_LOAD_NUMBER:
          text = `Resending invoice for reference #${customerOrderNumber} from ${companyName}`;
          break;
        case InvoiceSubjectLine.INVOICE_NUMBER_AND_CUSTOMER_LOAD:
          text = `Resending invoice #${invoiceNumber}, for reference #${customerOrderNumber} from ${companyName}`;
          break;
        default:
          break;
      }
    } else {
      const isQboInvoice = source === InvoiceSources.QBO && enableSendQboInvoiceInternally;
      text = isQboInvoice
        ? `Invoice #${invoiceNumber} from ${companyName}`
        : invoiceNumber
        ? `Invoice #${invoiceNumber} from ${companyName}`
        : `Invoice #[INVOICE_NUMBER] from ${companyName}`;

      switch (subjectLine) {
        case InvoiceSubjectLine.INVOICE_NUMBER:
          break;
        case InvoiceSubjectLine.CUSTOMER_LOAD_NUMBER:
          text = `Invoice for reference #${customerOrderNumber} from ${companyName}`;
          break;
        case InvoiceSubjectLine.INVOICE_NUMBER_AND_CUSTOMER_LOAD:
          text = isQboInvoice
            ? `Invoice #${invoiceNumber}, for reference #${customerOrderNumber} from ${companyName}`
            : invoiceNumber
            ? `Invoice #${invoiceNumber}, for reference #${customerOrderNumber} from ${companyName}`
            : `Invoice #[INVOICE_NUMBER], for reference #${customerOrderNumber} from ${companyName}`;
          break;
        default:
          break;
      }
    }

    setEmailSubject(text);
  }, [hasBeenSent, invoiceNumber, division?.name]);

  useEffect(() => {
    if (invoiceDialogClosed) {
      setEmailSubject(undefined);
      setEmailBody(undefined);
      setEmailWarning(true);
      setHasDefaultEmailBody(true);
    }
  }, [invoiceDialogClosed]);

  const handleClose = (): void => {
    setEmailSubject(undefined);
    setEmailBody(undefined);
    setEmailWarning(true);
    setHasDefaultEmailBody(true);
    onClose && onClose(true);
  };

  const handleOnBack = (): void => {
    onClose && onClose(false);
  };

  const handleOnSave = (): void => {
    onSaveClick && onSaveClick();
  };

  const handleOnSend = (): void => {
    const data: PreviewInvoiceSendData = {
      emailSubject: emailSubject,
      emailBody: emailBody,
      ccList: ccList
    };

    if (source === InvoiceSources.QBO && enableSendQboInvoiceInternally) {
      if (
        presetAdditionalQBOEmails &&
        presetAdditionalQBOEmails.length <= 0 &&
        presetQboEmailTo &&
        presetQboEmailTo.length <= 0
      ) {
        addNotification({
          message: 'Please fill in the Additional recipients field with at least one email',
          variant: 'error'
        });
        return;
      }

      onQBOInvoiceSent && onQBOInvoiceSent(invoiceId!, data);
      handleClose();
      return;
    }

    if (!!emailWarning) {
      logRocketTrack('Invoice', {
        action: 'Send Truckbase invoice from preview page',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Please fill in To field with at least one email',
        variant: 'error'
      });
      return;
    }
    // we don't want to close if there's data missing that can be added/changed
    const canSend = canSendInvoice && canSendInvoice(data);
    if (!canSend) {
      return;
    }

    onInvoiceSent && onInvoiceSent(data);
    handleClose();
  };

  const handleDelete = (): void => {
    onInvoiceDeleted && onInvoiceDeleted();
    handleClose();
  };

  /**
   * Returns example email body with customer name and tenant data
   * @param customerName
   * @param tenantData
   * @param divisionName
   * @returns string
   */
  const getPrefilledBody = (
    customerName: string,
    tenantData: { userName: string; companyName: string },
    tenantAccountingSettings: TenantAccountingSettings | undefined,
    divisionName?: string
  ): string => {
    return `Hello ${customerName}, 

${tenantAccountingSettings?.invoice_email_body ? tenantAccountingSettings?.invoice_email_body : DefaultInvoiceEmailBodyCopy}

Sincerely, 
${tenantData.userName}, ${divisionName ? divisionName : tenantData.companyName}`;
  };

  const previewActions = (): React.ReactElement[] => {
    if (source === InvoiceSources.TB) {
      return [
        <TwButton
          key="1"
          fullwidth={false}
          type={TwButtonType.secondary}
          label="Back"
          action={handleOnBack}
        />,
        <TwButton
          key="2"
          fullwidth={false}
          type={TwButtonType.primary}
          label={factorInvoice ? 'Email & send to factoring' : 'Send'}
          action={handleOnSend}
        />
      ];
    }

    if (source === InvoiceSources.QBO && enableSendQboInvoiceInternally) {
      return [
        <TwButton
          key="1"
          fullwidth={false}
          type={TwButtonType.text}
          label="Delete"
          action={(): void => {
            setDeleteInvoiceOpen(true);
          }}
          startIcon={<DeleteOutlineOutlinedIcon sx={{ fontSize: '16px' }} />}
          sx={{
            color: '#DC2626 !important'
          }}
        />,
        <TwButton
          key="2"
          fullwidth={false}
          type={TwButtonType.text}
          label="Edit"
          action={(): void => {
            onEditClick && onEditClick(invoiceId!);
          }}
          startIcon={<CreateOutlinedIcon sx={{ fontSize: '16px' }} />}
          sx={{ marginLeft: '8px' }}
        />,
        <TwButton
          key="3"
          fullwidth={false}
          type={TwButtonType.secondary}
          label="Save"
          action={handleOnSave}
          sx={{ marginLeft: '8px' }}
        />,
        <TwButton
          key="4"
          fullwidth={false}
          type={TwButtonType.primary}
          label="Send"
          action={handleOnSend}
          sx={{ marginLeft: '8px' }}
        />
      ];
    }

    return [];
  };

  return (
    <TwDialog
      open={open}
      showCloseIcon={source !== InvoiceSources.QBO || !enableSendQboInvoiceInternally}
      onClickClose={handleClose}
      title="Preview invoice"
      actionsAlignment={source === InvoiceSources.QBO ? 'flex-end' : 'space-between'}
      fullScreen={true}
      sx={{
        display: 'flex',
        position: 'unset'
      }}
      PaperProps={{
        sx: {
          position: 'absolute',
          width: '32%',
          top: 0,
          left: 0,
          margin: 0,
          height: '100vh',
          maxHeight: '100vh',
          zIndex: 1400
        }
      }}
      actions={source === InvoiceSources.TB ? previewActions() : undefined}
      fullWidthAction={
        source === InvoiceSources.QBO && enableSendQboInvoiceInternally ? (
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box>{previewActions()[0]}</Box>
            <Box>{previewActions().slice(1)}</Box>
          </Box>
        ) : undefined
      }
    >
      {loading ? (
        <TwBackdrop
          isOpen={!!loading}
          sx={{
            marginTop: '60%'
          }}
        />
      ) : (
        <>
          <Typography
            variant="body1"
            fontWeight="400"
            color="#6B7280"
            fontSize="14px"
            marginBottom="25px"
          >
            Note: You may customize the email subject and body by editing the fields below.
          </Typography>

          {source === InvoiceSources.TB && (
            <>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="400"
                  color="#6B7280"
                  fontSize="14px"
                  marginBottom="5px"
                  width="10%"
                >
                  To
                </Typography>
                <TwTagsField
                  id="preview-invoice-email-to-preset"
                  tagsDisplayed={presetEmailTo}
                  hasError={emailWarning}
                  tagType={'email'}
                  onChange={(_e, emails): void => {
                    setEmailWarning(emails && emails.length > 0 ? false : true);
                  }}
                  onTagAdded={(emails): void => {
                    onSelectedEmailTo && onSelectedEmailTo(emails);
                    emails.length > 0 ? setEmailWarning(false) : setEmailWarning(true);
                  }}
                  suggestedOptions={suggestedContacts}
                  sx={{ width: '100%' }}
                />
              </Box>

              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="400"
                  color="#6B7280"
                  fontSize="14px"
                  marginBottom="5px"
                  width="10%"
                >
                  Cc
                </Typography>
                <TwTagsField
                  id="preview-invoice-email-cc"
                  tagsDisplayed={presetEmailCc}
                  tagType={'email'}
                  onTagAdded={(emails): void => {
                    setCcList(emails);
                    onSelectedEmailCc && onSelectedEmailCc(emails);
                  }}
                  suggestedOptions={suggestedContacts}
                  sx={{ width: '100%' }}
                />
              </Box>
            </>
          )}

          {source === InvoiceSources.QBO && (
            <TwTagsField
              id="emailListQBOAdditionalRecipients"
              label="Additional recipients"
              tagsDisplayed={presetAdditionalQBOEmails}
              tagType={'email'}
              onTagAdded={(emails): void => {
                onSelectedAdditionalEmailsChange && onSelectedAdditionalEmailsChange(emails);
              }}
              suggestedOptions={suggestedContacts}
              placeholder="Optional"
            />
          )}

          <Divider
            sx={{
              margin: '20px 0'
            }}
          />

          <Typography
            variant="body1"
            fontWeight="400"
            color="#6B7280"
            fontSize="14px"
            marginTop="10px"
            marginBottom="5px"
          >
            <Box display="flex" alignItems="center" gap="4px">
              Email subject
              {source === InvoiceSources.TB && (
                <TwTooltip
                  title="Invoice number will be generated after the invoice is created and/or sent out"
                  placement="top"
                >
                  <InfoIcon
                    sx={{
                      color: '#D1D5DB',
                      fontSize: '14px'
                    }}
                  />
                </TwTooltip>
              )}
            </Box>
          </Typography>
          <TwField
            name={'invoice-preview-email-subject'}
            inputType={TwInputTypes.text}
            error={false}
            fullWidth={true}
            autoComplete="off"
            value={emailSubject}
            onInputChange={(e): void => {
              setEmailSubject(e.target.value);
            }}
            disabled={false}
          />

          <Typography
            variant="body1"
            fontWeight="400"
            color="#6B7280"
            fontSize="14px"
            marginTop="10px"
            marginBottom="5px"
          >
            Email body
          </Typography>
          <TwField
            name={'invoice-preview-email-body'}
            inputType={TwInputTypes.textarea}
            error={false}
            fullWidth={true}
            autoComplete="off"
            value={emailBody}
            onInputChange={(e): void => {
              setEmailBody(e.target.value);
              setHasDefaultEmailBody(false);
            }}
            disabled={false}
          />

          {attachments && attachments.length > 0 && (
            <Typography
              variant="body1"
              fontWeight="400"
              color="#6B7280"
              fontSize="14px"
              marginTop="10px"
              marginBottom="5px"
            >
              Attached documents
            </Typography>
          )}

          {attachments &&
            (attachments as OrderAttachment[])?.map((document) => (
              <DetailFile
                key={document.id}
                fileName={document.attachment.name}
                type={StringUtils.getLoadDocumentTypeLabel(document.type)}
                created_at={document.created_at ? new Date(document.created_at) : undefined}
                showMenu={false}
                driverVisibility={document.visibility}
                size={document.attachment.size}
                isEncrypted={document.is_encrypted}
                attachmentId={document.id}
              />
            ))}

          <TwDeleteInvoice
            open={deleteInvoiceOpen}
            invoiceId={invoiceId || ''}
            message="Are you sure you want to delete this invoice? This action will permanently remove the
              invoice both from Truckbase and QuickBooks. Once deleted, this action cannot be undone.
              Please confirm if you want to proceed to delete this invoice."
            onCancel={(): void => setDeleteInvoiceOpen(false)}
            onDelete={handleDelete}
          />
        </>
      )}
    </TwDialog>
  );
};

export default PreviewInvoice;
